import { ExpandMore, Lock, Receipt, Search } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { isUploadAllowed, isCashPayment } from "./constants";
import {
  resolveDefaultTab,
  mapFetchFinalBillRow,
  readLensApplicableStatus,
  buildLensApplicableStatusPayload,
  isLensLocked,
} from "./pureLogic";
import * as api from "./billCollection.service";
import CentralLensModal from "../../../central/component/CentralLensDetails/CentralLensModal";
import StatusPill from "./StatusPill";
import DocumentUploadTab from "./DocumentUploadTab";
import DocumentActionsTab from "./DocumentActionsTab";
import "./styles.css";

/**
 * Combined Bill & Collection Actions — inline Accordion.
 *
 * API calls fire ONLY when the accordion is expanded (not on page load).
 * This mirrors the central dashboard where calls fire on "Bill Collection" CTA click.
 */
const CombinedBillCollection = ({
  data: dataProp,
  onRefresh,
  onUpdateIpdNo,
  onUpdateInfo,
  onClose,
}) => {
  const reduxPatientInfo = useSelector(
    (state) => state.patients?.patientInfo?.allInfo
  );
  const data = dataProp || reduxPatientInfo || {};

  const [expanded, setExpanded] = useState(false);
  const hasLoadedOnce = useRef(false);

  const [activeMainTab, setActiveMainTab] = useState("upload");
  const [finalImgArray, setFinalImgArray] = useState([]);
  const [disableFileUpload, setDisableFileUpload] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [isDetailedBillRequired, setIsDetailedBillRequired] = useState(false);
  // Lens gate (driven entirely by getFinalBillCentral — backend decides
  // applicability). Mirrors the central dashboard behaviour.
  const [isLensApplicable, setIsLensApplicable] = useState(false);
  const [lensApplicableStatus, setLensApplicableStatus] = useState("");
  const [lensDetails, setLensDetails] = useState([]);
  // Local session state for the lens gate.
  const [lensChoice, setLensChoice] = useState(""); // "" | "Applicable" | "Not Applicable"
  const [lensSaved, setLensSaved] = useState(false);
  const [lensModalOpen, setLensModalOpen] = useState(false);
  // Snapshot of lensDetails length taken when the lens modal opens, used to
  // detect a successful save after the (reused) modal closes.
  const lensCountBeforeModal = useRef(0);
  const [loading, setLoading] = useState(true);
  const [searchAppId, setSearchAppId] = useState("");
  const [searching, setSearching] = useState(false);
  const [currentData, setCurrentData] = useState(data);

  const collectionStatus =
    typeof currentData?.collectionStatus === "object"
      ? currentData.collectionStatus?.status
      : currentData?.collectionStatus || "";
  const uploadAllowed = isUploadAllowed(collectionStatus);
  const isFinalBill = currentData?.tabKey === "Final Bill";
  const appId = currentData?.appID || "";
  const leadId = currentData?.leadId || currentData?.lead_id || "";

  // ── getFinalBillCentral: uploaded docs + statuses (called with a known appId) ──
  const loadDocs = useCallback(async (idOverride) => {
    const id = idOverride || appId;
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = (await api.getFinalBillCentral(id)) || {};
      const d = res?.data || {};
      const images = d.images || d.finalBillCentral?.images || [];
      setFinalImgArray(images);
      // These flags come back at the TOP LEVEL of the response
      // (res.isDetailedBillRequired, res.isLensApplicable, ...), NOT inside res.data.
      const lensDetailsList = Array.isArray(res.lensDetails) ? res.lensDetails : [];
      // Show the "Detailed Bill Required" note in Verification & Action when the
      // API flags it (central parity).
      setIsDetailedBillRequired(res.isDetailedBillRequired === true);
      // Lens gate: only active when the final bill flags lens as applicable.
      setIsLensApplicable(res.isLensApplicable === true);
      setLensApplicableStatus(readLensApplicableStatus(res.lensApplicableStatus));
      setLensDetails(lensDetailsList);
      if (d.notUploadFB === true) {
        setDisableFileUpload(true);
        toast.info("Insurance Has Approved the Final Bill");
      }
      if (d.isFinanceFBStatus === "Approved" || d.isInsFBStatus === "Approved") {
        setIsDisable(true);
        toast.info("Document already approved. Read-only mode.");
      }
      // Merge the top-level flags into the returned object so callers (e.g. the
      // lens modal close handler that reads lensDetails) get them regardless of
      // whether they live at the top level or inside data.
      return { ...d, lensDetails: lensDetailsList };
    } catch (err) {
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // On first expand: mirror the central flow →
  //   1) fetchFinalBill(appId) → rebuild the modal data from the row (data[0])
  //   2) getFinalBillCentral(appId) → uploaded docs + statuses
  const loadOnExpand = useCallback(async () => {
    if (!appId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.fetchFinalBill(appId);
      const raw = res?.data?.[0];
      if (raw) {
        const rebuilt = mapFetchFinalBillRow(raw);
        setCurrentData(rebuilt);
        await loadDocs(rebuilt.appID || appId);
        return;
      }
    } catch (err) {
      console.error(err);
    }
    // Fallback: no row → still try docs with the mapped appId.
    await loadDocs(appId);
  }, [appId, loadDocs]);

  // Fire API calls ONLY on first expand (lazy load).
  useEffect(() => {
    if (expanded && !hasLoadedOnce.current) {
      hasLoadedOnce.current = true;
      loadOnExpand();
    }
  }, [expanded, loadOnExpand]);

  // ── Resolve default tab once loading resolves ──
  useEffect(() => {
    if (loading) return;
    setActiveMainTab(resolveDefaultTab(uploadAllowed, finalImgArray.length));
  }, [loading, uploadAllowed, finalImgArray.length]);

  // ── Sync data when prop changes ──
  // Only mirror the incoming (sparse) prop BEFORE the accordion has done its
  // first expand-load. Once `fetchFinalBill` has rebuilt the rich row into
  // `currentData`, we must NOT clobber it with the sparse reporting row again
  // (that was wiping out the prefilled Actions-tab fields).
  useEffect(() => {
    if (dataProp && !hasLoadedOnce.current) setCurrentData(dataProp);
  }, [dataProp]);

  // ── Search / switch appointment ──
  const handleSearch = async () => {
    if (!searchAppId.trim()) return;
    setSearching(true);
    try {
      const res = await api.fetchFinalBill(searchAppId.trim());
      if (res?.data?.[0]) {
        const rebuilt = mapFetchFinalBillRow(res.data[0]);
        // Reset all internal state for the switched appointment (parity with the
        // central dashboard's rebuildFromRaw) so stale flags/docs from the
        // previous appointment don't leak into the new one.
        setFinalImgArray([]);
        setDisableFileUpload(false);
        setIsDisable(false);
        setIsDetailedBillRequired(false);
        setCurrentData(rebuilt);
        // Then run getFinalBillCentral for the new appId and bind its response
        // (images → finalImgArray, notUploadFB → disableFileUpload, approved → isDisable).
        await loadDocs(rebuilt.appID || searchAppId.trim());
        setSearchAppId("");
        toast.success(
          "Switched to appointment " + (rebuilt.appID || "")
        );
      } else {
        toast.info("No appointment found");
      }
    } catch {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleSaveData = async (result) => {
    if (!appId) return;
    setLoading(true);
    try {
      const payload = {
        app_id: appId,
        finalBillCentral: {
          images: result?.data || finalImgArray,
        },
      };
      if (isCashPayment(currentData?.payment_mode)) {
        payload.finalBillObj = {
          finalBillAmount:
            result?.finalBillAmount != null
              ? Number(result.finalBillAmount)
              : currentData?.finalBillAmount ?? null,
          netBillAmount: currentData?.netBillAmount ?? null,
        };
        payload.isInsFBStatus = "Approved";
      }
      const res = await api.uploadFinalBillCentral(payload);
      if (res?.status === 200 || res?.status === 1 || res?.status === "Success" || res?.message === "Success") {
        toast.success("Document decisions saved successfully");
        await loadDocs();
        onRefresh?.();
      } else {
        toast.error(res?.message || "Failed to save document decisions");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving document decisions");
    } finally {
      setLoading(false);
      // NOTE: do NOT close here. Save Data persists decisions but must keep the
      // modal/accordion open. The modal only closes on Send for Approval success.
    }
  };

  // Close the accordion/modal ONLY on a successful Send for Approval (from the
  // Collection Updation tab). Collapse the accordion and notify the parent.
  const handleApprovalSuccess = () => {
    setExpanded(false);
    onClose?.({ status: "approved" });
  };

  const handleAccordionChange = (_event, isExpanded) => {
    setExpanded(isExpanded);
  };

  // ── Lens gate handlers (mirror the central dashboard) ──

  // Persist the lens applicability decision. Returns true on success.
  const persistLensChoice = async (value) => {
    try {
      const payload = buildLensApplicableStatusPayload(
        appId,
        value,
        localStorage.getItem("userId")
      );
      const res = await api.updateCentralDashReporting(payload);
      const ok =
        res?.status === 200 ||
        res?.status === 1 ||
        res?.message === "Success";
      if (!ok) {
        toast.error("Could not save lens selection. Please try again.");
      }
      return ok;
    } catch (err) {
      console.error(err);
      toast.error("Could not save lens selection. Please try again.");
      return false;
    }
  };

  // User picked Applicable / Not Applicable in the Document Upload tab.
  const handleLensChoice = async (choice) => {
    setLensChoice(choice);
    setLensSaved(false);
    const ok = await persistLensChoice(choice);
    if (!ok) {
      setLensChoice(""); // revert so the tab stays locked; user can retry
      return;
    }
    if (choice === "Applicable") {
      lensCountBeforeModal.current = lensDetails.length;
      setLensModalOpen(true);
    }
  };

  // The reused CentralLensModal has no success callback; it always calls onClose
  // after its save attempt. Detect a successful save by re-fetching and checking
  // whether the lens details count increased.
  const handleLensModalClose = async () => {
    setLensModalOpen(false);
    const d = await loadDocs();
    const newCount = Array.isArray(d?.lensDetails) ? d.lensDetails.length : 0;
    if (newCount > lensCountBeforeModal.current) {
      setLensSaved(true);
      toast.success("Lens details saved. You can now proceed with the documents.");
    } else {
      setLensSaved(false);
      toast.error("Lens details not saved. Please enter lens details to continue.");
    }
  };

  const lensLocked = isLensLocked({
    isLensApplicable,
    lensApplicableStatus,
    lensChoice,
    lensSaved,
  });

  return (
    <Accordion
      className="cbc-accordion"
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        boxShadow: "0px 4px 10px 0px #75757533 !important",
        background: "#fff",
        borderRadius: "4px !important",
        m: "0px 16px 10px",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: "#3476CD" }} />}
        sx={{ minHeight: "45px" }}
        aria-controls="combined-bill-collection-content"
      >
        <Box sx={{ width: "100%" }}>
          <Box className="cbc-accordion-title">
            <Receipt sx={{ fontSize: 16, color: "#3476CD" }} />
            <Typography
              component="h2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#3476CD",
                lineHeight: 1.25,
              }}
            >
              Combined Bill &amp; Collection Actions
            </Typography>
            {collectionStatus && <StatusPill collectionStatus={collectionStatus} />}
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ borderTop: "0.5px solid #3476CD33", p: 0 }}>
        {/* ── Header bar (pills + search) ── */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          {appId && <span className="cbc-pill gray">App: {appId}</span>}
          {leadId && <span className="cbc-pill gray">Lead: {leadId}</span>}
          <Box sx={{ flex: 1 }} />
          <div className="cbc-search-box">
            <input
              value={searchAppId}
              onChange={(e) => setSearchAppId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Switch by Appointment ID"
              aria-label="Switch by Appointment ID"
            />
            <button
              className="cbc-btn outline"
              style={{ padding: "4px 8px", minHeight: 36 }}
              onClick={handleSearch}
              disabled={searching}
              aria-label="Search appointment"
            >
              {searching ? (
                <CircularProgress size={14} />
              ) : (
                <Search sx={{ fontSize: 16 }} />
              )}
            </button>
          </div>
        </Box>

        {/* ── Main tabs ── */}
        <div className="cbc-tabs">
          <button
            className={`cbc-tab ${activeMainTab === "upload" ? "active" : ""}`}
            onClick={() => uploadAllowed && setActiveMainTab("upload")}
            disabled={!uploadAllowed}
          >
            {!uploadAllowed && <Lock sx={{ fontSize: 12, mr: 0.5 }} />}
            Document Upload
          </button>
          <button
            className={`cbc-tab ${activeMainTab === "actions" ? "active" : ""}`}
            onClick={() => setActiveMainTab("actions")}
          >
            Document / Collection Actions
          </button>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className="cbc-loading">
            <CircularProgress size={28} />
          </div>
        ) : (
          <>
            {activeMainTab === "upload" && !uploadAllowed && (
              <div className="cbc-empty" style={{ padding: 32 }}>
                <Lock sx={{ fontSize: 32, color: "#d1d5db", mb: 1 }} />
                <div>Document upload is locked</div>
                <div style={{ marginTop: 4 }}>
                  Current status: <strong>{collectionStatus}</strong>
                </div>
              </div>
            )}

            {activeMainTab === "upload" && uploadAllowed && (
              <DocumentUploadTab
                data={currentData}
                finalImgArray={finalImgArray}
                setFinalImgArray={setFinalImgArray}
                disableFileUpload={disableFileUpload}
                onSubmitSuccess={async () => {
                  // Reload docs/statuses but STAY on the Document Upload tab.
                  // The user should always land/remain on Document Upload by
                  // default; we no longer auto-switch to Document / Collection
                  // Actions after a successful submit.
                  await loadDocs();
                  onRefresh?.();
                }}
              />
            )}

            {activeMainTab === "actions" && (
              <DocumentActionsTab
                data={currentData}
                urlArray={
                  currentData?.urlArray && currentData.urlArray.length
                    ? currentData.urlArray
                    : finalImgArray
                }
                isDisable={isDisable}
                isDetailedBillRequired={isDetailedBillRequired}
                isFinalBill={isFinalBill}
                // Lens gate moved here: shown in Collection Updation → Collectible
                // sub-tab, above "Collected at Hospital". Locks that sub-tab.
                isLensApplicable={isLensApplicable}
                hasLensDecision={!!(lensApplicableStatus && String(lensApplicableStatus).trim())}
                lensChoice={lensChoice}
                lensSaved={lensSaved}
                lensLocked={lensLocked}
                onLensChoice={handleLensChoice}
                onReopenLensModal={() => setLensModalOpen(true)}
                onSaveData={handleSaveData}
                onRefresh={onRefresh}
                onUpdateIpdNo={onUpdateIpdNo}
                onUpdateInfo={onUpdateInfo}
                onGoToUpload={() => {
                  if (uploadAllowed) setActiveMainTab("upload");
                  else toast.info("Document upload is locked for this status");
                }}
                onStatusChange={(newStatus) => {
                  // Sync the freshly-saved collection status up so the accordion
                  // header StatusPill (and upload gating) reflect it immediately.
                  setCurrentData((prev) => ({ ...prev, collectionStatus: newStatus }));
                }}
                onApprovalSuccess={handleApprovalSuccess}
              />
            )}
          </>
        )}

        {/* Reused Reporting lens modal (no duplicate). Opened only via the
            Document Upload lens gate when the user selects "Applicable". */}
        {lensModalOpen && (
          <CentralLensModal
            open={lensModalOpen}
            onClose={handleLensModalClose}
            onOpen={() => setLensModalOpen(true)}
            appointmentId={appId}
            // Wider only when opened from Combined Bill & Collection. Other
            // usages omit sx and keep the default width, so they're unaffected.
            sx={{ maxWidth: "720px", width: "90%" }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default CombinedBillCollection;
